import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Button,
  Modal,
  Separator,
  RevisionProductForm,
  Switch
} from '../../../../../../components';
import { AppContext } from '../../../../../../context/AppContext';
import { emptyRevisionProduct } from '../../../../../../emptyObjects';
import { useNavigate, useResource } from '../../../../../../hooks';
import { RevisionProduct, RevisionService } from '../../../../../../interfaces';
import CSVForm from './components';
import './styles.scss';

interface ProductPayload {
  normCode: string
  fase: string
  code: string
  brand: string
  presentation: string
  description: string
  package: string
  long: string
  high: string
  radius: string
}

interface Props {
  clientID: string
  collaboratorID: string
  revisionServiceID: string
  preloadedProduct?: RevisionProduct
  onChangeCurrentComponent: (_value: 'product-list') => void
}

export const ProductForm: React.FC<Props> = (props) => {
  const {
    createResource,
    updateResource
  } = useResource<RevisionProduct | RevisionProduct[]>();

  const navigate = useNavigate();

  const {
    clientID,
    collaboratorID,
    preloadedProduct = emptyRevisionProduct,
    onChangeCurrentComponent
  } = props;

  const { t } = useTranslation();

  const {
    apiType,
    setOpenModal,
    soundEffects
  } = useContext(AppContext);

  const [currentForm, setCurrentForm] = useState<'manual-form' | 'csv-form' | 'none'>('manual-form');
  const [revisionServiceID, setRevisionServiceID] = useState<string>(props.revisionServiceID);

  const buildBody = (serviceID: string, products: ProductPayload[]): any => {
    if (apiType === 'admin') {
      return {
        clientID,
        collaboratorID,
        revisionServiceID: serviceID,
        products
      };
    }

    return {
      revisionServiceID: serviceID,
      products
    };
  };

  const handleChangeProductInputMethod = (value: string | number) => {
    setCurrentForm(value as 'manual-form' | 'csv-form' | 'none');
  };

  const handleCreateConstacyService = (): Promise<RevisionService> => {
    return new Promise((resolve, reject) => {
      createResource(
        '/public/revision-services',
        {},
        (data: any) => {
          navigate(`/revision-request/step_2/${data.id}/${data.requestNumber}/${data.client.id}/${data.collaborator.id}`);
          setRevisionServiceID(data.id);
          resolve(data);
        },
        (error: string) => reject(error)
      );
    });
  };

  const handleCreateSingleProduct = (
    serviceID: string,
    product: RevisionProduct
  ): Promise<RevisionProduct> => {
    return new Promise((resolve, reject) => {
      createResource(
        `/${apiType}/revision-products`,
        buildBody(
          serviceID,
          [
            {
              ...product,
              package: product.indicators.package,
              long: product.indicators.long,
              high: product.indicators.high,
              radius: product.indicators.radio
            }
          ]
        ),
        (data: RevisionProduct | RevisionProduct[]) => {
          Modal.fireSuccess(
            t('global.correct'),
            t('services.registeredProduct'),
            setOpenModal,
            () => onChangeCurrentComponent('product-list'),
            soundEffects
          );

          resolve((data as RevisionProduct[])[0]);
        },
        (error: string) => reject(error)
      );
    });
  };

  const handleCreateManyProducts = (
    products: ProductPayload[],
    serviceID: string
  ): Promise<RevisionProduct[]> => {
    return new Promise((resolve, reject) => {
      createResource(
        `/${apiType}/revision-products`,
        buildBody(serviceID, products),
        (data: RevisionProduct | RevisionProduct[]) => {
          Modal.fireSuccess(
            t('global.correct'),
            t('services.registeredProducts'),
            setOpenModal,
            () => onChangeCurrentComponent('product-list'),
            soundEffects
          );
          resolve((data as RevisionProduct[]));
        },
        (error: string) => reject(error)
      );
    });
  };

  const handleUpdateProduct = async (product: RevisionProduct) => {
    updateResource(
      `/${apiType}/revision-products/${product.id}`,
      product,
      (_data: RevisionProduct | RevisionProduct[]) => {
        Modal.fireSuccess(
          t('global.correct'),
          t('services.updatedProduct'),
          setOpenModal,
          () => onChangeCurrentComponent('product-list'),
          soundEffects
        );
      },
      (error: string) => Modal.fireError(error, setOpenModal, soundEffects)
    );
  };

  const handleCreateProductAndServiceIfDoesntExist = async (product: RevisionProduct) => {
    try {
      if (revisionServiceID === 'none' && apiType === 'public') {
        const service = await handleCreateConstacyService();
        await handleCreateSingleProduct(service.id, product);
      } else {
        await handleCreateSingleProduct(revisionServiceID, product);
      }
    } catch (error) {
      Modal.fireError(error as string, setOpenModal, soundEffects);
    }
  };

  const handleCreateProductsAndServiceIfDoesntExist = async (products: ProductPayload[]) => {
    try {
      if (revisionServiceID === 'none' && apiType === 'public') {
        const service = await handleCreateConstacyService();
        await handleCreateManyProducts(products, service.id);
      } else {
        await handleCreateManyProducts(products, revisionServiceID);
      }
    } catch (error) {
      Modal.fireError(error as string, setOpenModal, soundEffects);
    }
  };

  const formType = {
    'manual-form': (<RevisionProductForm
      preloadedProduct={preloadedProduct}
      onCreateProduct={(product: RevisionProduct) => {
        handleCreateProductAndServiceIfDoesntExist(product);
      }}
      onUpdateProduct={(product: RevisionProduct) => {
        handleUpdateProduct(product);
      }}
    />),
    'csv-form': (<CSVForm onCreateProducts={handleCreateProductsAndServiceIfDoesntExist} />),
    none: (<p>{t('services.creationproductMethod')}</p>)
  };

  return (
    <div className='product-form'>
      <div className='product-form__top-container'>
        <Switch
          leftLabel={{
            text: t('services.creationproductMethodForm'),
            value: 'manual-form'
          }}
          rigthLabel={{
            text: t('services.creationproductMethodTemplate'),
            value: 'csv-form'
          }}
          onChange={handleChangeProductInputMethod}
        />
        <br />
        <Separator orientation='horizontal' />
        <br />
      </div>
      {formType[currentForm]}
      <br />
      <Button
        onClick={() => onChangeCurrentComponent('product-list')}
        type='primary'
        label={t('global.list') || ''}
        icon='leftArrow'
        iconPosition='left'
      />
    </div>
  );
};

export default ProductForm;
